import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Speech-to-text that only ever runs on the user's device.
 *
 * The legacy `webkitSpeechRecognition` in Safari, Chrome on Android, and
 * Samsung Internet streams captured audio to a vendor cloud service for
 * transcription. Dictating a secret there would send the plaintext to a third
 * party, so this hook refuses to run unless the browser exposes the on-device
 * API (Chrome and Edge desktop 139+) and confirms a local language pack. There
 * is deliberately no cloud fallback: where local recognition is unavailable the
 * hook reports `unsupported` and the caller renders nothing.
 */

/** Languages Chrome can recognize on-device. */
const ON_DEVICE_LANGS = [
  'de-DE', 'en-US', 'es-ES', 'fr-FR', 'hi-IN', 'id-ID', 'it-IT', 'ja-JP',
  'ko-KR', 'pl-PL', 'pt-BR', 'ru-RU', 'th-TH', 'tr-TR', 'vi-VN', 'zh-CN', 'zh-TW',
];

const DEFAULT_LANG = 'en-US';

function getRecognitionCtor() {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

/**
 * The static availability helpers only exist where recognition can run
 * locally. Their absence means the browser would transcribe in the cloud.
 */
function getOnDeviceCtor() {
  const Ctor = getRecognitionCtor();
  if (!Ctor || typeof Ctor.available !== 'function' || typeof Ctor.install !== 'function') {
    return null;
  }
  return Ctor;
}

/** Pick the closest on-device language to the browser's preference. */
function resolveLang() {
  const preferred = (typeof navigator !== 'undefined' && navigator.language) || DEFAULT_LANG;
  const exact = ON_DEVICE_LANGS.find((lang) => lang.toLowerCase() === preferred.toLowerCase());
  if (exact) return exact;

  const base = preferred.split('-')[0].toLowerCase();
  const sameBase = ON_DEVICE_LANGS.find((lang) => lang.split('-')[0].toLowerCase() === base);
  return sameBase || DEFAULT_LANG;
}

const ERROR_MESSAGES = {
  'not-allowed': 'Microphone access was blocked. Allow it in your browser settings to dictate.',
  'service-not-allowed': 'Microphone access was blocked. Allow it in your browser settings to dictate.',
  'audio-capture': 'No microphone was found.',
  'no-speech': 'Nothing was picked up. Try again and speak a little closer.',
  'language-not-supported': 'On-device dictation is not available for your language.',
  network: 'On-device dictation stopped unexpectedly. Type the secret instead.',
};

export default function useOnDeviceDictation({ onTranscript } = {}) {
  // 'checking' | 'unsupported' | 'idle' | 'downloadable' | 'downloading' | 'listening'
  const [status, setStatus] = useState('checking');
  const [interim, setInterim] = useState('');
  const [error, setError] = useState('');

  const recognitionRef = useRef(null);
  const langRef = useRef(DEFAULT_LANG);
  const mountedRef = useRef(true);
  const onTranscriptRef = useRef(onTranscript);

  useEffect(() => {
    onTranscriptRef.current = onTranscript;
  }, [onTranscript]);

  useEffect(() => {
    mountedRef.current = true;
    let cancelled = false;

    const Ctor = getOnDeviceCtor();
    if (!Ctor) {
      setStatus('unsupported');
      return () => {
        cancelled = true;
        mountedRef.current = false;
      };
    }

    langRef.current = resolveLang();

    Ctor.available({ langs: [langRef.current], processLocally: true })
      .then((availability) => {
        if (cancelled) return;
        if (availability === 'available') setStatus('idle');
        else if (availability === 'downloadable') setStatus('downloadable');
        else if (availability === 'downloading') setStatus('downloading');
        else setStatus('unsupported');
      })
      .catch(() => {
        if (!cancelled) setStatus('unsupported');
      });

    return () => {
      cancelled = true;
      mountedRef.current = false;
    };
  }, []);

  const stop = useCallback(() => {
    const recognition = recognitionRef.current;
    if (recognition) {
      recognitionRef.current = null;
      try {
        recognition.abort();
      } catch {
        // Already stopped.
      }
    }
    setInterim('');
    setStatus((current) => (current === 'listening' ? 'idle' : current));
  }, []);

  useEffect(() => () => {
    const recognition = recognitionRef.current;
    recognitionRef.current = null;
    if (recognition) {
      try {
        recognition.abort();
      } catch {
        // Already stopped.
      }
    }
  }, []);

  const installLanguagePack = useCallback(async () => {
    const Ctor = getOnDeviceCtor();
    if (!Ctor) {
      setStatus('unsupported');
      return false;
    }

    setError('');
    setStatus('downloading');
    try {
      const installed = await Ctor.install({ langs: [langRef.current], processLocally: true });
      const availability = await Ctor.available({ langs: [langRef.current], processLocally: true });
      const ready = availability === 'available';
      if (!mountedRef.current) return ready;

      if (ready) {
        setStatus('idle');
      } else if (installed && availability === 'downloading') {
        setStatus('downloading');
      } else {
        setStatus('downloadable');
        setError('The language pack could not be installed. Type the secret instead.');
      }
      return ready;
    } catch {
      if (mountedRef.current) {
        setStatus('downloadable');
        setError('The language pack could not be installed. Type the secret instead.');
      }
      return false;
    }
  }, []);

  const start = useCallback(() => {
    const Ctor = getOnDeviceCtor();
    if (!Ctor) {
      setStatus('unsupported');
      return;
    }

    setError('');
    setInterim('');

    const recognition = new Ctor();
    recognition.processLocally = true;

    // A browser that silently drops the flag would transcribe in the cloud.
    if (recognition.processLocally !== true) {
      setStatus('unsupported');
      return;
    }

    recognition.lang = langRef.current;
    recognition.continuous = true;
    recognition.interimResults = true;

    recognition.onresult = (event) => {
      let finalText = '';
      let pending = '';

      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        const { transcript } = result[0];
        if (result.isFinal) finalText += transcript;
        else pending += transcript;
      }

      setInterim(pending);
      if (finalText.trim() && onTranscriptRef.current) {
        onTranscriptRef.current(finalText.trim());
      }
    };

    recognition.onerror = (event) => {
      if (!mountedRef.current) return;
      if (event.error === 'aborted') return;
      setError(ERROR_MESSAGES[event.error] || 'Dictation stopped unexpectedly.');
      recognitionRef.current = null;
      setInterim('');
      setStatus('idle');
    };

    recognition.onend = () => {
      if (!mountedRef.current) return;
      recognitionRef.current = null;
      setInterim('');
      setStatus((current) => (current === 'listening' ? 'idle' : current));
    };

    try {
      recognition.start();
      recognitionRef.current = recognition;
      setStatus('listening');
    } catch {
      setError('Could not start dictation. Try again.');
      setStatus('idle');
    }
  }, []);

  const toggle = useCallback(async () => {
    if (status === 'listening') {
      stop();
      return;
    }
    if (status === 'downloadable') {
      const ready = await installLanguagePack();
      if (ready) start();
      return;
    }
    if (status === 'idle') {
      start();
    }
  }, [status, stop, installLanguagePack, start]);

  return {
    status,
    interim,
    error,
    isSupported: status !== 'unsupported' && status !== 'checking',
    isListening: status === 'listening',
    isBusy: status === 'downloading',
    toggle,
    stop,
  };
}
